import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { DashboardShell } from "../../components/DashboardShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

export default function AdminDashboard() {
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [sortKey, setSortKey] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "clinician", credentials: "", license_state: "" });
  const [testClient, setTestClient] = useState({ first_name: "", last_name: "", email: "", password: "",
    phone: "", dob: "", address: "", product_slug: "workplace", clinician_id: "", qa_reviewer_id: "" });
  const [testClients, setTestClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [creatingTest, setCreatingTest] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };

  const sortedUsers = useMemo(() => {
    const val = (u) => {
      switch (sortKey) {
        case "name": return (u.name || "").toLowerCase();
        case "email": return (u.email || "").toLowerCase();
        case "role": return u.role || "";
        case "credentials": return (u.credentials || "").toLowerCase();
        default: return "";
      }
    };
    return [...users].sort((a, b) => {
      const cmp = String(val(a)).localeCompare(String(val(b)));
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [users, sortKey, sortDir]);

  const SortHeader = ({ label, k, className = "" }) => (
    <th className={`px-6 py-4 ${className}`}>
      <button type="button" onClick={() => toggleSort(k)} data-testid={`sort-${k}`}
        className="inline-flex items-center gap-1.5 uppercase tracking-[0.15em] hover:text-[#1E3F20] transition-colors">
        {label}
        {sortKey === k
          ? (sortDir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)
          : <ArrowUpDown className="h-3 w-3 opacity-40" />}
      </button>
    </th>
  );

  const load = () => {
    api.get("/admin/overview").then((r) => setOverview(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/admin/users").then((r) => setUsers(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/admin/test-clients").then((r) => setTestClients(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/users", form);
      toast.success(`${form.role === "admin" ? "Admin" : "Clinician"} account created.`);
      setForm({ name: "", email: "", password: "", role: "clinician", credentials: "", license_state: "" });
      load();
    } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setTc = (k, v) => setTestClient((f) => ({ ...f, [k]: v }));

  const createTestClient = async (e) => {
    e.preventDefault();
    setCreatingTest(true);
    setTestResult(null);
    try {
      const payload = { ...testClient };
      if (!payload.clinician_id) delete payload.clinician_id;
      if (!payload.qa_reviewer_id) delete payload.qa_reviewer_id;
      const { data } = await api.post("/admin/test-client", payload);
      setTestResult(data);
      toast.success(`Test client ready — ${data.email} can log in and start the evaluation immediately (no payment).`);
      setTestClient({ first_name: "", last_name: "", email: "", password: "", phone: "", dob: "",
        address: "", product_slug: "workplace", clinician_id: "", qa_reviewer_id: "" });
      load();
    } catch (err) { toast.error(errMsg(err)); } finally { setCreatingTest(false); }
  };

  const testClientAction = async (id, action) => {
    const verb = action === "archive" ? "Archive" : action === "convert" ? "Convert to STANDARD (payment required for future evaluations)" : "Restore";
    if (!window.confirm(`${verb} this test client?`)) return;
    try {
      await api.post(`/admin/test-clients/${id}/${action}`);
      toast.success(`Test client ${action}d.`);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const clinicians = users.filter((u) => u.role === "clinician");
  const admins = users.filter((u) => u.role === "admin");

  return (
    <DashboardShell title="Practice overview" subtitle="Operations at a glance for the Assessment Center.">
      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-12" data-testid="admin-stats">
          {[
            ["Total evaluations", overview.total_cases], ["Revenue", `$${overview.revenue.toLocaleString()}`],
            ["In QA review", overview.qa_pending], ["Clinicians", overview.clinicians],
            ["Clients", overview.clients], ["Delivered", overview.by_status?.delivered || 0],
          ].map(([k, v]) => (
            <div key={k} className="card-warm p-5" data-testid={`stat-${k.toLowerCase().replace(/[^a-z]/g, "-")}`}>
              <p className="text-[11px] tracking-[0.15em] uppercase font-bold text-[#5C5C57]">{k}</p>
              <p className="font-heading text-3xl font-semibold mt-1">{v}</p>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="space-y-8">
        <form onSubmit={create} className="card-warm p-6 space-y-4 h-fit" data-testid="create-clinician-form">
          <p className="font-heading text-xl font-semibold">Create staff account</p>
          <div className="space-y-2"><Label>Full name</Label>
            <Input required data-testid="staff-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} className="input-warm border-[#E5E5DF]" /></div>
          <div className="space-y-2"><Label>Email</Label>
            <Input type="email" required data-testid="staff-email-input" value={form.email} onChange={(e) => set("email", e.target.value)} className="input-warm border-[#E5E5DF]" /></div>
          <div className="space-y-2"><Label>Temporary password</Label>
            <Input required minLength={8} data-testid="staff-password-input" value={form.password} onChange={(e) => set("password", e.target.value)} className="input-warm border-[#E5E5DF]" /></div>
          <div className="space-y-2"><Label>Role</Label>
            <Select value={form.role} onValueChange={(v) => set("role", v)}>
              <SelectTrigger data-testid="staff-role-select" className="border-[#E5E5DF]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="clinician">Clinician</SelectItem>
                <SelectItem value="admin">Admin / QA</SelectItem>
              </SelectContent>
            </Select></div>
          <div className="space-y-2"><Label>Credentials (e.g., LCSW-C)</Label>
            <Input data-testid="staff-credentials-input" value={form.credentials} onChange={(e) => set("credentials", e.target.value)} className="input-warm border-[#E5E5DF]" /></div>
          <div className="space-y-2"><Label>License state</Label>
            <Input data-testid="staff-state-input" value={form.license_state} onChange={(e) => set("license_state", e.target.value)} className="input-warm border-[#E5E5DF]" /></div>
          <Button type="submit" disabled={saving} data-testid="create-staff-btn"
            className="w-full rounded-full bg-[#1E3F20] hover:bg-[#2C5A2E] text-white transition-colors">
            {saving ? "Creating…" : "Create account"}
          </Button>
        </form>

        <form onSubmit={createTestClient} className="card-warm p-6 space-y-4 h-fit" data-testid="create-test-client-form">
          <div className="flex items-center justify-between">
            <p className="font-heading text-xl font-semibold">Create new client</p>
            <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-[#B8860B]/15 text-[#B8860B]" data-testid="test-mode-badge">TEST / NO-PAYMENT</span>
          </div>
          <p className="text-xs text-[#5C5C57]">Skips payment server-side — the client can log in immediately and walk the full evaluation process (intake → questionnaires → booking → interview → report → QA → PDF). Clearly typed as a TEST account everywhere.</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>First name</Label>
              <Input required data-testid="test-client-first-name" value={testClient.first_name} onChange={(e) => setTc("first_name", e.target.value)} className="input-warm border-[#E5E5DF]" /></div>
            <div className="space-y-2"><Label>Last name</Label>
              <Input required data-testid="test-client-last-name" value={testClient.last_name} onChange={(e) => setTc("last_name", e.target.value)} className="input-warm border-[#E5E5DF]" /></div>
          </div>
          <div className="space-y-2"><Label>Client email</Label>
            <Input type="email" required data-testid="test-client-email" value={testClient.email} onChange={(e) => setTc("email", e.target.value)} className="input-warm border-[#E5E5DF]" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>Password (8+)</Label>
              <Input required minLength={8} data-testid="test-client-password" value={testClient.password} onChange={(e) => setTc("password", e.target.value)} className="input-warm border-[#E5E5DF]" /></div>
            <div className="space-y-2"><Label>Phone</Label>
              <Input data-testid="test-client-phone" value={testClient.phone} onChange={(e) => setTc("phone", e.target.value)} className="input-warm border-[#E5E5DF]" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>Date of birth</Label>
              <Input type="date" data-testid="test-client-dob" value={testClient.dob} onChange={(e) => setTc("dob", e.target.value)} className="input-warm border-[#E5E5DF]" /></div>
            <div className="space-y-2"><Label>Address</Label>
              <Input data-testid="test-client-address" value={testClient.address} onChange={(e) => setTc("address", e.target.value)} className="input-warm border-[#E5E5DF]" /></div>
          </div>
          <div className="space-y-2"><Label>Evaluation (comped — $0)</Label>
            <Select value={testClient.product_slug} onValueChange={(v) => setTc("product_slug", v)}>
              <SelectTrigger data-testid="test-client-product" className="border-[#E5E5DF]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {products.map((p) => <SelectItem key={p.slug} value={p.slug}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>Clinician (optional)</Label>
              <Select value={testClient.clinician_id || "__none__"} onValueChange={(v) => setTc("clinician_id", v === "__none__" ? "" : v)}>
                <SelectTrigger data-testid="test-client-clinician" className="border-[#E5E5DF]"><SelectValue placeholder="Assign later" /></SelectTrigger>
                <SelectContent><SelectItem value="__none__">Assign later</SelectItem>
                  {clinicians.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-2"><Label>QA reviewer (optional)</Label>
              <Select value={testClient.qa_reviewer_id || "__none__"} onValueChange={(v) => setTc("qa_reviewer_id", v === "__none__" ? "" : v)}>
                <SelectTrigger data-testid="test-client-qa" className="border-[#E5E5DF]"><SelectValue placeholder="Any QA admin" /></SelectTrigger>
                <SelectContent><SelectItem value="__none__">Any QA admin</SelectItem>
                  {admins.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <Button type="submit" disabled={creatingTest} data-testid="create-test-client-btn"
            className="w-full rounded-full bg-[#7E9B82] hover:bg-[#6b8a6f] text-white transition-colors">
            {creatingTest ? "Creating…" : "Create client"}
          </Button>
          {testResult && (
            <div className="rounded-lg bg-[#2C5A2E]/10 border border-[#2C5A2E]/30 p-4 text-xs space-y-1" data-testid="test-client-result">
              <p><span className="font-semibold">Login:</span> {testResult.email}</p>
              <p><span className="font-semibold">Case:</span> #{testResult.case_id.slice(-8)} {testResult.created_user ? "(new account)" : "(existing account — case added)"}</p>
            </div>
          )}
        </form>
        </div>

        <div className="lg:col-span-2 card-warm overflow-hidden h-fit" data-testid="users-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs tracking-[0.15em] uppercase text-[#5C5C57] border-b border-[#E5E5DF]">
                <SortHeader label="Name" k="name" /><SortHeader label="Email" k="email" />
                <SortHeader label="Role" k="role" /><SortHeader label="Credentials" k="credentials" className="hidden md:table-cell" />
                <th className="px-6 py-4">Type</th>
              </tr>
            </thead>
            <tbody>
              {sortedUsers.map((u) => (
                <tr key={u.id} className="border-b border-[#E5E5DF] last:border-0" data-testid={`user-row-${u.id}`}>
                  <td className="px-6 py-3">
                    <Link to={`/admin/users/${u.id}`} data-testid={`user-link-${u.id}`}
                      className="font-semibold text-[#1E3F20] hover:underline">{u.name}</Link>
                  </td>
                  <td className="px-6 py-3 text-[#5C5C57]">{u.email}</td>
                  <td className="px-6 py-3 capitalize">{u.role}</td>
                  <td className="px-6 py-3 hidden md:table-cell text-[#5C5C57]">{u.credentials || "—"}{u.license_state ? ` · ${u.license_state}` : ""}</td>
                  <td className="px-6 py-3">
                    {u.account_type === "TEST" ? (
                      <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-[#B8860B]/15 text-[#B8860B]" data-testid={`test-badge-${u.id}`}>TEST CLIENT</span>
                    ) : (
                      <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-[#E3DAC9]/60 text-[#5C5C57]">STANDARD</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {testClients.length > 0 && (
        <section className="mt-12" data-testid="test-clients-panel">
          <p className="label-caps text-[#B8860B] mb-4">Test clients ({testClients.length})</p>
          <div className="card-warm overflow-hidden border-[#B8860B]/30">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs tracking-[0.15em] uppercase text-[#5C5C57] border-b border-[#E5E5DF]">
                  <th className="px-6 py-3">Client</th><th className="px-6 py-3">Email</th>
                  <th className="px-6 py-3">Evaluations</th><th className="px-6 py-3">Status</th><th className="px-6 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {testClients.map((t) => (
                  <tr key={t.id} className="border-b border-[#E5E5DF] last:border-0" data-testid={`test-client-row-${t.id}`}>
                    <td className="px-6 py-3">
                      <span className="font-semibold">{t.name}</span>
                      <span className="ml-2 text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#B8860B]/15 text-[#B8860B]">TEST CLIENT</span>
                    </td>
                    <td className="px-6 py-3 text-[#5C5C57]">{t.email}</td>
                    <td className="px-6 py-3">{t.active_case_count} active / {t.case_count} total</td>
                    <td className="px-6 py-3">{t.active === false ? <span className="text-[#8C2A2A] font-semibold">Archived</span> : <span className="text-[#2C5A2E] font-semibold">Active</span>}</td>
                    <td className="px-6 py-3 text-right space-x-2">
                      {t.active === false ? (
                        <Button size="sm" variant="outline" data-testid={`test-restore-${t.id}`} onClick={() => testClientAction(t.id, "restore")}
                          className="rounded-full border-[#2C5A2E]/40 text-[#2C5A2E] h-7 text-xs">Restore</Button>
                      ) : (
                        <Button size="sm" variant="outline" data-testid={`test-archive-${t.id}`} onClick={() => testClientAction(t.id, "archive")}
                          className="rounded-full border-[#8C2A2A]/40 text-[#8C2A2A] h-7 text-xs">Archive</Button>
                      )}
                      <Button size="sm" variant="outline" data-testid={`test-convert-${t.id}`} onClick={() => testClientAction(t.id, "convert")}
                        className="rounded-full border-[#1E3F20]/30 text-[#1E3F20] h-7 text-xs">Convert to standard</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </DashboardShell>
  );
}
